"use client"

import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { tokenManager } from "@/lib/token-manager"

type AuthContextType = {
  token: string | null
  setToken: (token: string | null) => void
  isAuthenticated: boolean
  tokenSource: "url" | "parent" | "none"
  requestTokenFromParent: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [tokenSource, setTokenSource] = useState<"url" | "parent" | "none">("none")
  const [tokenRequested, setTokenRequested] = useState<boolean>(false)

  // Function to request token from parent window
  const requestTokenFromParent = () => {
    // Prevent duplicate requests
    if (tokenRequested) {
      return
    }

    if (typeof window !== "undefined" && window.parent && window.parent !== window) {
      try {
        window.parent.postMessage({
          action: "REQUEST_TOKEN"
        }, "*")
        setTokenRequested(true)
      } catch (error) {
        console.error("Failed to send REQUEST_TOKEN to parent:", error)
      }
    }
  }

  // Wrapper function to update token and notify token manager
  const setToken = (newToken: string | null) => {
    setTokenState(newToken)
    tokenManager.notifyTokenUpdate(newToken)
  }

  // Parse URL parameters on initial load to extract the token
  useEffect(() => {
    const extractTokenFromUrl = () => {
      if (typeof window !== "undefined") {
        const urlParams = new URLSearchParams(window.location.search)
        const tokenFromUrl = urlParams.get("token")

        if (tokenFromUrl) {
          setTokenSource("url")
          setToken(tokenFromUrl)
          return { token: tokenFromUrl, source: "url" as const }
        }

        return { token: null, source: "none" as const }
      }
      return { token: null, source: "none" as const }
    }

    const { token: urlToken } = extractTokenFromUrl()
    setIsAuthenticated(!!urlToken)
    
    // If no token from URL, request from parent (only on initial load)
    if (!urlToken) {
      setTimeout(() => requestTokenFromParent(), 100) // Small delay to ensure parent is ready
    }
  }, []) // Empty dependency array - only run on initial mount

  // Listen for messages from parent window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Process messages from parent window
      try {
        const data = event.data

        // Check if this is a SET_TOKEN message
        if (data && data.action === "SET_TOKEN" && typeof data.token === "string") {
          // Set the token source first, then the token
          setTokenSource("parent")
          setToken(data.token)
          setTokenRequested(false) // Reset flag since we got a token
        } else if (data && data.action === "TOKEN_EXPIRED") {
          // Clear current token and request a new one
          setToken(null)
          setTokenSource("none")
          setIsAuthenticated(false)
          setTokenRequested(false) // Reset flag before requesting new token
          requestTokenFromParent()
        }
      } catch (error) {
        console.error("Error processing message from parent:", error)
      }
    }

    // Add event listener for messages
    if (typeof window !== "undefined") {
      window.addEventListener("message", handleMessage)
    }

    // Clean up event listener
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("message", handleMessage)
      }
    }
  }, [])

  // Update authentication status when token changes
  useEffect(() => {
    setIsAuthenticated(!!token)
  }, [token])

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        isAuthenticated,
        tokenSource,
        requestTokenFromParent,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
} 